import type { ImplementationEngagement } from './booking/booking-service';
import type { InterviewState } from './interview/model';

/**
 * Magasin **de démonstration** des engagements d'implémentation, en mémoire du navigateur : relie
 * la page de rendez-vous, l'entretien et le back-office AYUMIA tant que l'API n'existe pas. Il
 * n'est ni persistant ni partagé (rien dans le stockage du navigateur) ; l'API le remplacera par les
 * objets `implementation-engagement` et `implementation-session` (plan §5, §6).
 */

export type EngagementStatus = 'scheduled' | 'preparing' | 'ready' | 'in_progress' | 'completed' | 'cancelled';

export interface PreparationEntry {
  kind: 'file' | 'system' | 'note' | 'skipped';
  label: string;
  at: string;
}

export interface EngagementRecord {
  engagement: ImplementationEngagement;
  status: EngagementStatus;
  consultant_requested: boolean;
  preparation: PreparationEntry[];
  /** Dernier état de l'entretien, s'il a commencé. */
  interview?: InterviewState;
  booked_at: string;
}

type Listener = () => void;

export class ImplementationStore {
  private readonly records = new Map<string, EngagementRecord>();
  private readonly listeners = new Set<Listener>();
  private version = 0;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Jeton de version pour `useSyncExternalStore`. */
  snapshot = (): number => this.version;

  list(): EngagementRecord[] {
    return [...this.records.values()].sort((a, b) => a.engagement.interview.scheduled_at.localeCompare(b.engagement.interview.scheduled_at));
  }

  get(id: string): EngagementRecord | undefined {
    return this.records.get(id);
  }

  put(record: EngagementRecord): void {
    this.records.set(record.engagement.engagement_id, record);
    this.changed();
  }

  update(id: string, change: (record: EngagementRecord) => EngagementRecord): void {
    const record = this.records.get(id);
    if (record === undefined) return;
    this.records.set(id, change(record));
    this.changed();
  }

  private changed(): void {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }
}

/** Identifiant de l'engagement de démonstration, auquel l'entretien de démonstration se rattache. */
export const DEMO_ENGAGEMENT_ID = '66f1c0ffee0000000000d0e1';

/** Données fictives pour que le back-office ne soit pas vide : aucune donnée réelle de client. */
export function seedDemoStore(store: ImplementationStore, now: Date): void {
  const day = (offset: number, hour: number): string => {
    const date = new Date(now);
    date.setUTCDate(date.getUTCDate() + offset);
    date.setUTCHours(hour, 0, 0, 0);
    return date.toISOString();
  };
  const engagement = (id: string, legal_name: string, country: string, contact: string, start: string, locale: string, facts: ImplementationEngagement['pre_discovery']['facts']): ImplementationEngagement => ({
    engagement_id: id,
    company: { legal_name, country },
    primary_contact: { name: contact, email: `contact@${legal_name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}.example` },
    interview: { scheduled_at: start, ends_at: new Date(Date.parse(start) + 3_600_000).toISOString(), timezone: 'Europe/Paris', locale, status: 'scheduled' },
    declared_context: { current_systems: [] },
    pre_discovery: { status: 'not_started', facts },
    simulated: true,
  });
  const declared = (key: string, value: string | string[], locale = 'fr') => ({
    domain: key.split('.')[0]!,
    key,
    value,
    epistemic_status: 'declared' as const,
    validation_status: 'unvalidated' as const,
    source_kind: 'customer_declaration' as const,
    locale: locale as 'fr',
  });

  store.put({
    engagement: {
      ...engagement(DEMO_ENGAGEMENT_ID, 'Maison Exemple SAS', 'FR', 'Gérant (fictif)', day(0, 7), 'fr', [
        declared('business_scope.target_domains', ['inventory', 'production', 'quality', 'traceability']),
        declared('integrations.current_systems', ['odoo']),
        declared('business_scope.industry', 'food'),
        declared('organization.user_count_band', '6-20'),
      ]),
      company: { legal_name: 'Maison Exemple SAS', country: 'FR', registration: { country: 'FR', scheme: 'FR_SIREN', value: '000000000' }, website: 'https://maison-exemple.example/' },
      declared_context: { current_systems: ['odoo'], primary_objective: 'Suivre les lots de la réception à la vente.' },
    },
    status: 'ready',
    consultant_requested: false,
    preparation: [{ kind: 'note', label: 'Nous avons ouvert une boutique en septembre.', at: day(-2, 9) }],
    booked_at: day(-3, 10),
  });
  store.put({
    engagement: engagement('66f1c0ffee0000000000d0e2', 'Fromagerie des Cimes (fictive)', 'CH', 'Responsable qualité (fictif)', day(2, 8), 'fr', [
      declared('business_scope.target_domains', ['production', 'quality', 'traceability']),
      declared('integrations.current_systems', ['spreadsheet', 'paper']),
    ]),
    status: 'scheduled',
    consultant_requested: true,
    preparation: [],
    booked_at: day(-1, 15),
  });
  store.put({
    engagement: engagement('66f1c0ffee0000000000d0e3', 'Atelier Textile Démo (fictif)', 'BE', 'Directrice des opérations (fictive)', day(5, 12), 'nl', [
      declared('business_scope.target_domains', ['sales', 'inventory', 'integrations'], 'nl'),
      declared('integrations.current_systems', ['shopify', 'other:Exact Online'], 'nl'),
    ]),
    status: 'scheduled',
    consultant_requested: false,
    preparation: [{ kind: 'skipped', label: 'Préparation passée par le client', at: day(-1, 16) }],
    booked_at: day(-1, 16),
  });
}
